import { useState, useEffect } from "react";
import { assetService } from "@/services/asset-service";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Edit3, Save, X, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

interface CsvEditorProps {
  /** A local file to be previewed - this would be used for previewing files before upload */
  localFile?: File;
  /** The UUID of the CSV asset to be previewed. */
  assetId?: string;
}

export const CsvEditor = ({ localFile, assetId }: CsvEditorProps) => {
  const [csvData, setCsvData] = useState<string[][]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [fileName, setFileName] = useState("");

  useEffect(() => {
    const fetchAndParseCsv = async () => {
      if (localFile) {
        // Handle local file preview
        try {
          setFileName(localFile.name);
          const text = await localFile.text();
          
          // Simple parsing for header and rows
          const lines = text.split(/\r?\n/).filter(line => line.trim() !== '');
          if (lines.length > 0) {
            setHeaders(lines[0].split(',').map(header => header.trim()));
            setCsvData(lines.slice(1).map(line => line.split(',').map(cell => cell.trim())));
          }
        } catch (error) {
          console.error("Error reading local CSV file:", error);
          toast.error("Failed to read CSV file: " + (error as Error).message);
        } finally {
          setLoading(false);
        }
      } else if (assetId) {
        // Handle stored asset preview
        try {
          setLoading(true);
          const asset = await assetService.getAsset(assetId);
          setFileName(asset.name);

          // Construct the download URL based on asset structure
          let dataUrl = `/d/${assetId}.csv`;
          
          // If asset.data has a URI, use that instead
          if (asset.data && typeof asset.data === 'object' && 'uri' in asset.data) {
            const uri = asset.data.uri as string;
            if (uri.startsWith('/d/')) {
              dataUrl = uri;
            } else if (uri.startsWith('http')) {
              dataUrl = uri;
            } else {
              dataUrl = `/${uri}`;
            }
          }

          // Get the asset data
          const response = await fetch(dataUrl);
          
          if (!response.ok) {
            throw new Error(`Failed to fetch file: ${response.statusText}`);
          }

          const csvText = await response.text();
          
          // Simple parsing for header and rows
          const lines = csvText.split(/\r?\n/).filter(line => line.trim() !== '');
          if (lines.length > 0) {
            setHeaders(lines[0].split(',').map(header => header.trim()));
            setCsvData(lines.slice(1).map(line => line.split(',').map(cell => cell.trim())));
          }
        } catch (error) {
          console.error("Error loading CSV:", error);
          toast.error("Failed to load CSV file: " + (error as Error).message);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchAndParseCsv();
  }, [localFile, assetId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (csvData.length === 0 || headers.length === 0) {
    return (
      <div className="text-center py-12">
        <FileSpreadsheet className="mx-auto h-12 w-12 text-gray-400" />
        <p className="mt-4 text-lg font-medium">No CSV data available</p>
        <p className="text-gray-500">The CSV file could not be loaded or is empty.</p>
      </div>
    );
  }

  const csvDataWithHeaders = headers.length > 0 ? [headers, ...csvData] : csvData;

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            {fileName}
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            {csvData.length} row{csvData.length !== 1 ? 's' : ''} × {headers.length} column{headers.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isEditing ? (
            <div className="flex gap-2">
              <Button variant="default" size="sm">
                <Save className="h-4 w-4 mr-2" />
                Save
              </Button>
              <Button onClick={() => setIsEditing(false)} variant="outline" size="sm">
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </div>
          ) : (
            <Button onClick={() => setIsEditing(true)} variant="default" size="sm">
              <Edit3 className="h-4 w-4 mr-2" />
              Edit
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table className="min-w-full">
            <TableHeader>
              {headers.length > 0 && (
                <TableRow className="bg-gray-100">
                  {headers.map((header, index) => (
                    <TableHead key={index} className="px-4 py-2 text-left font-semibold text-gray-700 border-r last:border-r-0">
                      {header || `Column ${index + 1}`}
                    </TableHead>
                  ))}
                </TableRow>
              )}
            </TableHeader>
            <TableBody>
              {csvData.map((row, rowIndex) => (
                <TableRow 
                  key={rowIndex} 
                  className={`border-b hover:bg-gray-50 transition-colors ${rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                >
                  {row.map((cell, cellIndex) => (
                    <TableCell 
                      key={cellIndex} 
                      className="px-4 py-2 border-r last:border-r-0 text-sm"
                    >
                      {cell}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};